import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { ProfilePreview } from './ProfilePreview';

/**
 * Global Profile Preview provider — mounts a single ProfilePreview modal
 * at the app root, exposed via `useProfilePreview()`. Any component can
 * open the preview without owning local state:
 *
 *   const { open } = useProfilePreview();
 *   <button onClick={() => open(uid)}>{name}</button>
 *
 * Why this and not per-screen modals: every avatar in the app needs the
 * same modal behavior (W/L stats + friend actions + view full). Owning
 * one mount at the root means the modal survives route changes and
 * doesn't fight the in-game/lobby z-index stack.
 */

/**
 * Where the preview was opened from. The sheet can now file a REPORT, and a
 * report that cannot say which game it happened in is nearly unactionable for
 * whoever reads the queue — so every caller gets to name its surface. It is
 * optional, and defaults to `profile`, because an un-contextualised report is
 * still far better than no report at all.
 */
export interface PreviewContext {
  contextType?: 'game' | 'thread' | 'name' | 'profile';
  contextId?: string | null;
}

interface ProfilePreviewContextValue {
  open: (uid: string | null, ctx?: PreviewContext) => void;
  close: () => void;
}

const Ctx = createContext<ProfilePreviewContextValue>({ open: () => {}, close: () => {} });

export function useProfilePreview(): ProfilePreviewContextValue {
  return useContext(Ctx);
}

export function ProfilePreviewProvider({ children }: { children: ReactNode }) {
  const [uid, setUid] = useState<string | null>(null);
  const [ctx, setCtx] = useState<PreviewContext>({});

  const open = useCallback((nextUid: string | null, nextCtx: PreviewContext = {}) => {
    if (nextUid && nextUid !== 'undefined') {
      setUid(nextUid);
      setCtx(nextCtx);
    }
  }, []);
  const close = useCallback(() => setUid(null), []);

  return (
    <Ctx.Provider value={{ open, close }}>
      {children}
      {uid && (
        <ProfilePreview
          uid={uid}
          onClose={close}
          contextType={ctx.contextType}
          contextId={ctx.contextId}
        />
      )}
    </Ctx.Provider>
  );
}
